import { FC, useCallback, useEffect, useTransition } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PageContextWrapper } from "@ryde/controls";
import { useYupValidationResolver } from "@ryde/redux";
import {
  activeStationsListSelector,
  BOOKING_ADD_NEW_FORM_FIELDS,
  BOOKING_BULK_FORM_FIELDS,
  bookingBulkCreateStateFromPollingState,
  bulkApprovalBookingConflictsAtom,
  convertUTCToISO,
  datePartWithoutTimeYYYYMMDD,
  DirectionEnum,
  dropdownAtomFamily,
  IAddNewBulkBookingsForm,
  IAddNewBulkBookingsProps,
  IBulkSetBookingsDto,
  IBulkSetBookingsPassengerDto,
  IDropdownItemShifts,
  IInjectedDrawerProps,
  isBulkBookingSetFinishSelector,
  isValidDateTime,
  lastChosenBulkCreateBookingDirectionAtom,
  systemSettingsDtoAtom,
  unique,
  useDrawers,
  useHasPermissions,
  useTimezone,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../../common-default-drawer";
import { BaseDrawer } from "../../drawer";
import {
  BulkCreateBookingFooter,
  getApprovalBookingConflictAPI,
  getBulkBookingDirectionMultiplier,
  isMealOnlyShiftSelected,
  IUseBulkCreateGetPayloadParams,
  PreviewStepForm,
  useBulkCreateBooking,
} from "../bulk-bookings-components";
import { AddNewBulkBookingsForm } from "./add-new-bulk-bookings-form";
import { getAddNewBulkBookingsValidationSchemaAccordingToPermission } from "./add-new-bulk-bookings.validation";

export const AddNewBulkBookings: FC<
  IAddNewBulkBookingsProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber, passengerId, date }) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const activeStationsList = useRecoilValue(activeStationsListSelector);
  const { hasPermissions: hasPermissionCreateInThePast } = useHasPermissions({
    entity: "booking",
    action: "createInPast",
  });
  const addNewBulkBookingsValidationSchema =
    getAddNewBulkBookingsValidationSchemaAccordingToPermission(
      hasPermissionCreateInThePast,
      timezone,
    );

  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { disableShiftsDropdown } = systemSettingsDto?.metadata ?? {};
  const resolver = useYupValidationResolver(
    addNewBulkBookingsValidationSchema,
    "booking",
  );
  const approvalBookingConflicts = useRecoilValue(
    bulkApprovalBookingConflictsAtom,
  );
  const { newBookingsIds, isLoading: isBulkBookingSetProceeding } =
    useRecoilValue(bookingBulkCreateStateFromPollingState);
  const isBulkBookingSetFinish = useRecoilValue(isBulkBookingSetFinishSelector);

  const lastChosenBulkCreateBookingDirection = useRecoilValue(
    lastChosenBulkCreateBookingDirectionAtom,
  );
  const shiftDropdownItems: IDropdownItemShifts[] = useRecoilValue(
    dropdownAtomFamily("shift"),
  );
  const { openBookingProduction } = useDrawers();
  const [, startTransition] = useTransition();

  const bulkApprovalBookingConflictsApproved = Array.from(
    approvalBookingConflicts.values(),
  )
    .flat()
    .filter(({ approvalReason }) => !approvalReason);

  const approvalBookingConflictsCount =
    bulkApprovalBookingConflictsApproved.length;

  const methods = useForm<IAddNewBulkBookingsForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      direction: disableShiftsDropdown
        ? (lastChosenBulkCreateBookingDirection ?? DirectionEnum.Both)
        : DirectionEnum.Both,
      shouldReplaceExisting: false,
      // RYDE-7139: removed with the "existing ride" conflict option (superseded by shouldReplaceExisting)
      // conflictsDetailsCreateIfExisting: "false",
      conflictsDetailsCreateIfInside: "false",
      conflictsDetailsCreateIfOverlap: "false",
      conflictsDetailsCreateIfGap: false,
      conflictsDetailsCreateIfIneligible: "false",
    },
    resolver,
  });
  const {
    getValues,
    trigger,
    setValue,
    formState: { isDirty },
    watch,
  } = methods;
  const {
    direction: directionFromForm,
    passengers,
    shiftId: shiftIdFromForm,
  } = getValues();
  const directionMultiplier = getBulkBookingDirectionMultiplier({
    direction: directionFromForm,
    isMealOnlyShift: isMealOnlyShiftSelected({
      shiftDropdownItems,
      shiftId: shiftIdFromForm,
    }),
  });
  const bookingCount = (passengers?.length ?? 0) * directionMultiplier;
  const successfullyBooked = bookingCount - approvalBookingConflictsCount;

  const getPayload = useCallback(
    ({
      isPreview,
      approvalConflicts,
    }: IUseBulkCreateGetPayloadParams): IBulkSetBookingsDto => {
      const {
        passengers: passengersValue,
        date: formDate,
        shouldReplaceExisting,
        // conflictsDetailsCreateIfExisting, // RYDE-7139: no longer destructured/sent (superseded by shouldReplaceExisting)
        conflictsDetailsCreateIfInside,
        conflictsDetailsCreateIfOverlap,
        conflictsDetailsCreateIfGap,
        conflictsDetailsCreateIfIneligible,
        ...formValues
      } = getValues();

      const passengersData: IBulkSetBookingsPassengerDto[] =
        passengersValue.map((passengerItemId: number) => {
          return {
            passengerId: passengerItemId,
            date: datePartWithoutTimeYYYYMMDD(formDate, timezone),
            direction: formValues.direction,
            arrivalTime:
              formValues.direction === DirectionEnum.Both ||
              formValues.direction === DirectionEnum.PickUp
                ? formValues.arrivalTime
                : undefined,
            departureTime:
              formValues.direction === DirectionEnum.Both ||
              formValues.direction === DirectionEnum.DropOff
                ? formValues.departureTime
                : undefined,
            shiftId: formValues.shiftId ?? undefined,
            flightId: formValues.flightId ?? undefined,
            mealId: formValues.mealId ?? undefined,

            originStationIdA: formValues.originStationIdA ?? undefined,
            destinationStationIdA:
              formValues.destinationStationIdA ?? undefined,
            originStationIdB: formValues.originStationIdB ?? undefined,
            destinationStationIdB:
              formValues.destinationStationIdB ?? undefined,
            hubIdA: formValues.hubStationA ?? undefined,
            hubIdB: formValues.hubStationB ?? undefined,
          };
        });

      const payload: IBulkSetBookingsDto = {
        passengersData,
        isPreview,
        shouldReplaceExisting,
        conflicts: {
          // createIfExisting: conflictsDetailsCreateIfExisting === "true", // RYDE-7139: superseded by shouldReplaceExisting
          createIfInside: conflictsDetailsCreateIfInside === "true",
          createIfOverlap: conflictsDetailsCreateIfOverlap === "true",
          createIfGap: conflictsDetailsCreateIfGap,
          createIfIneligible: conflictsDetailsCreateIfIneligible === "true",
        },
        approvalBookingConflicts:
          getApprovalBookingConflictAPI(approvalConflicts),
      };

      return payload;
    },
    [getValues, timezone],
  );

  const {
    onCloseClick,
    saveLabel,
    onSaveClick,
    cancelLabel,
    onCancelClick,
    isPreviewStep,
    refreshHandler,
    abortHandler,
    setBulkBookingStateToForm,
    fetchBookings,
    fetchPassengerStationsAndSetValue,
  } = useBulkCreateBooking<IAddNewBulkBookingsForm>({
    onCloseDrawer,
    getPayload,
    isDirty,
    trigger,
  });

  const passengersIdx = watch(BOOKING_ADD_NEW_FORM_FIELDS.passengers);
  const shiftId = watch(BOOKING_ADD_NEW_FORM_FIELDS.shiftId);
  const direction = watch(BOOKING_ADD_NEW_FORM_FIELDS.direction);
  const isSaveDisabled =
    isPreviewStep && (isBulkBookingSetProceeding || successfullyBooked <= 0);
  useEffect(() => {
    if (!passengersIdx) {
      return;
    }

    if (passengersIdx.length > 1) {
      setValue(BOOKING_BULK_FORM_FIELDS.originStationIdA, null);
      setValue(BOOKING_BULK_FORM_FIELDS.originStationIdB, null);
      setValue(BOOKING_BULK_FORM_FIELDS.destinationStationIdA, null);
      setValue(BOOKING_BULK_FORM_FIELDS.destinationStationIdB, null);
      return;
    }

    const fetchAndSet = async (): Promise<void> => {
      const result = await fetchPassengerStationsAndSetValue();

      if (result.success) {
        // TODO code duplication with `bulk-create-booking
        const passengerStations = result.data || [];

        passengerStations.forEach(passengerStationResult => {
          const isPickUp =
            passengerStationResult.direction === DirectionEnum.PickUp;
          const originStationField = isPickUp
            ? BOOKING_BULK_FORM_FIELDS.originStationIdA
            : BOOKING_BULK_FORM_FIELDS.originStationIdB;
          const destinationStationField = isPickUp
            ? BOOKING_BULK_FORM_FIELDS.destinationStationIdA
            : BOOKING_BULK_FORM_FIELDS.destinationStationIdB;

          if (
            activeStationsList.some(
              s => s?.value === passengerStationResult.originStationId,
            )
          ) {
            setValue(
              originStationField,
              passengerStationResult.originStationId,
            );
          }

          if (
            activeStationsList.some(
              s => s?.value === passengerStationResult.destinationStationId,
            )
          ) {
            setValue(
              destinationStationField,
              passengerStationResult.destinationStationId,
            );
          }
        });
      }
    };

    startTransition(() => {
      fetchAndSet();
    });
  }, [
    fetchPassengerStationsAndSetValue,
    passengersIdx,
    setValue,
    shiftId,
    direction,
    activeStationsList,
  ]);

  useEffect(() => {
    if (passengerId !== undefined) {
      setValue(BOOKING_ADD_NEW_FORM_FIELDS.passengers, [passengerId], {
        shouldTouch: true,
        shouldDirty: true,
      });
    }

    if (date && isValidDateTime(date)) {
      setValue(BOOKING_ADD_NEW_FORM_FIELDS.date, convertUTCToISO(date), {
        shouldTouch: true,
        shouldDirty: true,
      });
    }
  }, [date, passengerId, setValue]);

  const isProduceButtonActive = Boolean(
    isPreviewStep && newBookingsIds?.length && isBulkBookingSetFinish,
  );

  const onProduce = useEvent(async (): Promise<void> => {
    const handleProduceResult = await openBookingProduction({
      bookingsIds: unique(newBookingsIds),
      direction: getValues(BOOKING_ADD_NEW_FORM_FIELDS.direction),
    });

    if (handleProduceResult.success) {
      onCloseDrawer();
      setBulkBookingStateToForm();
      fetchBookings();
    }
  });

  return (
    <BaseDrawer onCloseDrawer={onCloseClick} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t("booking.bulk.setBookings")}
        onCloseDrawer={onCloseClick}
        Footer={
          <BulkCreateBookingFooter
            saveLabel={saveLabel}
            onSaveClick={onSaveClick}
            cancelLabel={cancelLabel}
            onCancelClick={onCancelClick}
            isSaveDisabled={isSaveDisabled}
            isLoading={isBulkBookingSetProceeding}
            isProduceButtonActive={isProduceButtonActive}
            onProduce={onProduce}
          />
        }
      >
        <PageContextWrapper entity="booking">
          <FormProvider {...methods}>
            {isPreviewStep ? (
              <PreviewStepForm
                bookingCount={bookingCount}
                onRefresh={refreshHandler}
                successfullyBooked={successfullyBooked}
                onSaveClick={onSaveClick}
                onAbort={abortHandler}
              />
            ) : (
              <AddNewBulkBookingsForm />
            )}
          </FormProvider>
        </PageContextWrapper>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
